import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import {
  MessageSquare,
  Search,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";

const PER_PAGE = 10;
const API = process.env.REACT_APP_API_URL ;

const statusStyles = {
  NEW: "bg-blue-100 text-blue-800 border-blue-200",
  IN_PROGRESS: "bg-amber-100 text-amber-800 border-amber-200",
  RESOLVED: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

const AdminContactQueries = () => {
  const [queries, setQueries] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchQueries();
  }, []);

  useEffect(() => {
    const f = queries.filter(
      (q) =>
        q.full_name?.toLowerCase().includes(search.toLowerCase()) ||
        q.email?.toLowerCase().includes(search.toLowerCase()) ||
        q.inquiry_type?.toLowerCase().includes(search.toLowerCase())
    );
    setFiltered(f);
    setPage(1);
  }, [search, queries]);

  const fetchQueries = async () => {
    try {
      const res = await axios.get(`${API}/api/contact/contact-queries`);
      setQueries(res.data || []);
    } catch (err) {
      console.error("Failed to fetch queries", err);
      toast.error("Could not load contact queries");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/api/contact/contact-queries/${id}/status`, {
        status,
      });
      setQueries((prev) =>
        prev.map((q) => (q.id === id ? { ...q, status } : q))
      );
      toast.success("Status updated");
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const sendReply = async () => {
    if (!reply.trim()) {
      toast.error("Reply cannot be empty");
      return;
    }

    try {
      const res = await axios.post(
        `${API}/api/contact/contact-queries/${selected.id}/reply`,
        { reply }
      );
      if (res.data?.success) {
        toast.success("Reply sent successfully");
        setReply("");
        setSelected(null);
      } else {
        toast.error("Failed to send reply");
      }
    } catch (err) {
      toast.error("Error sending reply");
    }
  };

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const data = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  return (
    <div className="min-h-screen bg-gray-50 pb-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <MessageSquare size={28} className="text-rose-600" />
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Contact Queries
            </h1>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
            />
            <input
              type="text"
              placeholder="Search by name, email or subject..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 outline-none transition"
            />
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">
              Loading queries...
            </div>
          ) : data.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {search ? "No matching queries found" : "No queries yet"}
            </div>
          ) : (
            <>
              {/* Desktop Table – hidden below ~640px */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full min-w-[900px]">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-5 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        ID
                      </th>
                      <th className="px-5 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-5 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-5 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Subject
                      </th>
                      <th className="px-5 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Message
                      </th>
                      <th className="px-5 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-5 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.map((q, i) => (
                      <tr key={q.id} className="hover:bg-gray-50/70">
                        <td className="px-5 py-4 text-sm text-gray-600 whitespace-nowrap">
                          {(page - 1) * PER_PAGE + i + 1}
                        </td>
                        <td className="px-5 py-4 font-medium text-gray-900">
                          {q.full_name}
                        </td>
                        <td className="px-5 py-4 text-sm text-gray-700 break-all">
                          {q.email}
                        </td>
                        <td className="px-5 py-4 text-sm text-gray-700">
                          {q.inquiry_type}
                        </td>
                        <td className="px-5 py-4 text-sm text-gray-700">
                          <button
                            onClick={() => setSelected(q)}
                            className="text-rose-600 hover:text-rose-800 font-medium hover:underline"
                          >
                            View message
                          </button>
                        </td>
                        <td className="px-5 py-4">
                          <select
                            value={q.status}
                            onChange={(e) => updateStatus(q.id, e.target.value)}
                            className={`w-full max-w-[140px] rounded-md px-3 py-1.5 text-sm font-medium border focus:ring-rose-500 focus:border-rose-500 ${statusStyles[q.status] || "bg-gray-100 text-gray-700"}`}
                          >
                            <option value="NEW">New</option>
                            <option value="IN_PROGRESS">In Progress</option>
                            <option value="RESOLVED">Resolved</option>
                          </select>
                        </td>
                        <td className="px-5 py-4 text-sm text-gray-500 whitespace-nowrap">
                          {new Date(q.created_at).toLocaleDateString("en-IN", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards – visible below ~640px */}
              <div className="sm:hidden divide-y divide-gray-200">
                {data.map((q, i) => (
                  <div key={q.id} className="p-4 hover:bg-gray-50/60">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="text-xs text-gray-500">
                          #{(page - 1) * PER_PAGE + i + 1}
                        </div>
                        <div className="font-medium text-gray-900 mt-0.5">
                          {q.full_name}
                        </div>
                        <div className="text-sm text-gray-600 mt-0.5 break-all">
                          {q.email}
                        </div>
                      </div>

                      <select
                        value={q.status}
                        onChange={(e) => updateStatus(q.id, e.target.value)}
                        className={`rounded px-2.5 py-1 text-xs font-medium border ${statusStyles[q.status] || "bg-gray-100 text-gray-700"}`}
                      >
                        <option value="NEW">New</option>
                        <option value="IN_PROGRESS">In Progress</option>
                        <option value="RESOLVED">Resolved</option>
                      </select>
                    </div>

                    <div className="text-sm text-gray-700 mt-2">
                      <span className="font-medium">Subject:</span>{" "}
                      {q.inquiry_type}
                    </div>

                    <div className="mt-3 flex items-center gap-3">
                      <button
                        onClick={() => setSelected(q)}
                        className="text-rose-600 hover:text-rose-800 text-sm font-medium flex items-center gap-1"
                      >
                        View message
                      </button>
                      <span className="text-xs text-gray-500">
                        {new Date(q.created_at).toLocaleDateString("en-IN", {
                          day: "numeric",
                          month: "short",
                        })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-4 py-5 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-sm text-gray-600">
                    Page <strong>{page}</strong> of <strong>{totalPages}</strong>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                      className="p-2.5 rounded-lg border disabled:opacity-40 hover:bg-gray-100 disabled:cursor-not-allowed transition"
                    >
                      <ChevronLeft size={18} />
                    </button>

                    <span className="px-4 py-2.5 bg-rose-600 text-white rounded-lg font-medium min-w-[3rem] text-center">
                      {page}
                    </span>

                    <button
                      onClick={() => setPage(page + 1)}
                      disabled={page === totalPages}
                      className="p-2.5 rounded-lg border disabled:opacity-40 hover:bg-gray-100 disabled:cursor-not-allowed transition"
                    >
                      <ChevronRight size={18} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* View + Reply Modal */}
      {selected && (
  <div 
    className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-3 xs:p-4 sm:p-6"
  >
    <div 
      className="
        bg-white rounded-xl 
        w-full max-w-[95vw] xs:max-w-[92vw] sm:max-w-lg md:max-w-xl 
        max-h-[94vh] overflow-y-auto
        p-4 xs:p-5 sm:p-6
      "
    >
      <div className="flex items-start justify-between gap-4 mb-3 sm:mb-4">
        <div className="min-w-0 flex-1">
          <h3 className="text-lg xs:text-xl sm:text-2xl font-semibold text-gray-900 truncate">
            {selected.full_name}
          </h3>
          <p className="text-xs xs:text-sm text-gray-600 mt-0.5 break-all">
            {selected.email}
          </p>
        </div>

        {/* Optional close button for better mobile UX */}
        <button
          onClick={() => setSelected(null)}
          className="p-1.5 -mt-1 -mr-1.5 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100 transition"
          aria-label="Close"
        >
          <X size={20} className="sm:size-24" />
        </button>
      </div>

      <div className="bg-gray-50 p-3 xs:p-4 rounded-md text-gray-800 mb-4 text-sm xs:text-base leading-relaxed whitespace-pre-wrap break-words">
        {selected.message}
      </div>

      <textarea
        rows={4}
        placeholder="Type your reply..."
        value={reply}
        onChange={(e) => setReply(e.target.value)}
        className="
          w-full border border-gray-300 rounded-md 
          p-2.5 xs:p-3 
          text-sm xs:text-base
          focus:ring-2 focus:ring-rose-500 focus:border-rose-500 
          outline-none resize-y min-h-[100px] sm:min-h-[120px]
          mb-4
        "
      />

      <div className="flex flex-col xs:flex-row gap-3 xs:gap-4 justify-end">
        <button
          onClick={() => setSelected(null)}
          className="
            px-5 xs:px-6 py-2.5 
            border border-gray-300 rounded-lg 
            text-sm xs:text-base font-medium
            hover:bg-gray-50 transition
            order-2 xs:order-1
          "
        >
          Cancel
        </button>

        <button
          onClick={sendReply}
          className="
            px-5 xs:px-6 py-2.5 
            bg-rose-600 hover:bg-rose-700 
            text-white rounded-lg 
            text-sm xs:text-base font-medium
            transition shadow-sm
            order-1 xs:order-2
          "
        >
          Send Reply
        </button>
      </div>
    </div>
  </div>
)}
    </div>
  );
};

export default AdminContactQueries;